import { Component, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton, type IColumn, Separator } from "@fluentui/react";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
    type VehicleGroup,
    type UserGroup,
    type User,
    type UserGroupUpdate,
    SortDirection,
    UserSortKey,
} from "../../api";

import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { ModalConfirmation } from "../atoms/modal-confirmation";
import { MultiSelectWithList } from "../organisms/multi-select-with-list";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";

import { createUuid, type UUID } from "../../utils/uuid";
import { doubleBindString } from "../../utils/double-bind";
import { compareSets } from "../../utils/set-comparison";
import sizes from "../sizes.scss";
import { defaultPageSize } from "../../utils/constants";

export interface FormUpdateUserGroupProps extends FormUpdateProps<UserGroup> {}

import { store } from "../../store";

@observer
export class FormUpdateUserGroup extends Component<FormUpdateUserGroupProps> {
    @observable private accessor cancelConfirmationModalVisible = false;

    // This list contains the ids of the assignments that already exist for this group.
    // I.e. the Users and VehicleGroups that have been selected for assignments.
    @observable private accessor originalUsers: UUID[] = [];
    @observable private accessor originalVehicleGroups: UUID[] = [];

    // These lists contain the ids of the assignments that're currently selected in the
    // multi-select dropdown.
    //
    // By comparing these with the original lists above, we can determine the changeset
    // of removed and added entities.
    @observable private accessor selectedUsers: UUID[] = [];
    @observable private accessor selectedVehicleGroups: UUID[] = [];

    // The list state ids for the respective lists of added groups.
    private userListStateId = createUuid();
    private vehicleGroupListStateId = createUuid();

    private validationId = createUuid();

    constructor(props: FormUpdateUserGroupProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryUserGroups.validation.update.updateModel(
            this.validationId,
            this.userGroupUpdate,
        );
    }

    componentDidMount(): void {
        store.repositoryUserGroups.validation.update.initializeModel(
            this.validationId,
            this.userGroupUpdate,
            this.props.id,
        );

        store.serviceListStates.users.initializeList(this.userListStateId, {
            query: () => {
                return { pageSize: defaultPageSize };
            },
            ignoreUrl: true,
        });
        store.serviceListStates.vehicleGroups.initializeList(this.vehicleGroupListStateId, {
            query: () => {
                return { pageSize: defaultPageSize };
            },
            ignoreUrl: true,
        });

        this.getInitialAssignments();
    }

    public componentWillUnmount(): void {
        store.repositoryUserGroups.discardMutableCopy(FormUpdateUserGroup.name, this.props.id);
    }

    /**
     * Get the Users and VehileGroups that're already assigned to this UserGroup.
     * This is done asynchronous once when initializing this component.
     *
     * Marking this function with @action.bound doesn't work as it's `async`.
     */
    async getInitialAssignments(): Promise<void> {
        // Get the list of all assigned VehicleGroups
        const vehicleGroupsMetaData = await store.repositoryVehicleGroups.fetchMetadata({
            pageSize: defaultPageSize,
            vehicleGroupIds: [this.props.id],
        });
        const vehicleGroups = await store.repositoryVehicleGroups.byQueryAsync(
            {
                pageSize: vehicleGroupsMetaData.totalCount,
                userGroupIds: [this.props.id],
            },
            { offset: 0, count: vehicleGroupsMetaData.totalCount },
        );
        this.originalVehicleGroups = vehicleGroups.map((vehicleGroup) => vehicleGroup.id);

        const userMetaData = await store.repositoryUsers.fetchMetadata({
            pageSize: defaultPageSize,
            userGroupIds: [this.props.id],
            sortKey: UserSortKey.LAST_NAME,
            sortDirection: SortDirection.ASCENDING,
        });
        const users = await store.repositoryUsers.byQueryAsync(
            {
                pageSize: userMetaData.totalCount,
                userGroupIds: [this.props.id],
            },
            { offset: 0, count: userMetaData.totalCount },
        );
        this.originalUsers = users.map((userGroup) => userGroup.id);

        runInAction(() => {
            this.selectedUsers = this.originalUsers;
            this.selectedVehicleGroups = this.originalVehicleGroups;
        });
    }

    @computed private get userGroup(): UserGroup | undefined {
        return store.repositoryUserGroups.mutableCopyById(FormUpdateUserGroup.name, this.props.id);
    }

    @computed private get userGroupUpdate(): UserGroupUpdate {
        const vehicleGroupChanges = compareSets(
            this.originalVehicleGroups,
            this.selectedVehicleGroups,
        );
        const userChanges = compareSets(this.originalUsers, this.selectedUsers);

        const update = {
            label: this.userGroup?.label,
            addVehicleGroupIds: vehicleGroupChanges.added,
            removeVehicleGroupIds: vehicleGroupChanges.removed,
            addUserIds: userChanges.added,
            removeUserIds: userChanges.removed,
        } as UserGroupUpdate;

        return update;
    }

    @action.bound
    private async updateUserGroup(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const update = this.userGroupUpdate;
        const userGroup = await store.repositoryUserGroups.update(this.props.id, update);

        // Reload Queries in here.
        // This cannot be done in the repositories itself, as they would have circular
        // dependencies on each other otherwise.
        if (update.addVehicleGroupIds || update.removeVehicleGroupIds) {
            store.repositoryVehicleGroups.reloadQuery({
                pageSize: defaultPageSize,
            });
            store.repositoryVehicleGroups.reloadQuery({
                pageSize: defaultPageSize,
                userGroupIds: [this.props.id],
            });
        }
        if (update.addUserIds || update.removeUserIds) {
            store.repositoryUsers.reloadQuery({
                pageSize: defaultPageSize,
            });
            store.repositoryUsers.reloadQuery({
                pageSize: defaultPageSize,
                userGroupIds: [this.props.id],
            });
        }

        this.reset();
        this.props.onUpdate(userGroup);
    }

    /**
     * This is run if the user clicks the form's cancel button.
     * A confirmation modal might pop up if there are any changes.
     */
    @action.bound private showConfirmAndCallDialogCancelCallback(): void {
        // Compare the assignments
        const userGroupChanges = compareSets(
            this.originalVehicleGroups,
            this.selectedVehicleGroups,
        );
        const vehicleChanges = compareSets(this.originalUsers, this.selectedUsers);

        // If there were any changes in assignments, ask the user if they really want to cancel.
        if (userGroupChanges.differs() || vehicleChanges.differs()) {
            this.cancelConfirmationModalVisible = true;
            return;
        }

        this.props.onDialogCancel();
    }

    /**
     * Actually close the component and reset all state.
     */
    @action.bound private closeForm(): void {
        this.reset();
        this.cancelConfirmationModalVisible = false;
        this.props.onDialogCancel();
    }

    @action.bound private reset(): void {
        this.originalUsers = [];
        this.originalVehicleGroups = [];
        this.selectedUsers = [];
        this.selectedVehicleGroups = [];
    }

    @action.bound private closeCancelConfirmationModalVisible(): void {
        this.cancelConfirmationModalVisible = false;
    }

    /**
     * Handle the actual addition of all currently selected Vehicles in the multi-select field.
     */
    @action.bound private addUsers(users: UUID[]): void {
        this.selectedUsers = [...this.selectedUsers, ...users];
    }

    /**
     * Handle the removal of Users.
     * In this case, removal means that they'll be removed from the list of added Users.
     */
    @action.bound private removeUsers(idsToRemove: UUID[]): void {
        const remainingUsers = this.selectedUsers.filter((id) => !idsToRemove.includes(id));
        this.selectedUsers = remainingUsers;
    }

    /**
     * Handle the actual addition of all currently selected VehicleGroups in the multi-select field.
     */
    @action.bound private addVehicleGroups(vehicleGroups: UUID[]): void {
        this.selectedVehicleGroups = [...this.selectedVehicleGroups, ...vehicleGroups];
    }

    /**
     * Handle the removal of VehicleGroups.
     * In this case, removal means that they'll be removed from the list of added VehicleGroups.
     */
    @action.bound private removeVehicleGroups(idsToRemove: UUID[]): void {
        const remainingGroups = this.selectedVehicleGroups.filter(
            (id) => !idsToRemove.includes(id),
        );
        this.selectedVehicleGroups = remainingGroups;
    }

    /**
     * The items that should be shown in the list of added VehicleGroups.
     */
    @computed private get vehicleGroupItems(): object[] {
        const groups = [];

        for (const id of this.selectedVehicleGroups) {
            const group = store.repositoryVehicleGroups.byId(id);
            if (group !== undefined) {
                groups.push({
                    key: group.id,
                    label: group.label,
                    assignedVehicleCount: group.assignedVehicleCount,
                    assignedUserGroupCount: group.assignedUserGroupCount,
                });
            }
        }

        return groups;
    }

    /**
     * The columns that should be shown in the list of selected VehicleGroups.
     */
    @computed private get vehicleGroupColumns(): IColumn[] {
        return [
            {
                fieldName: "label",
                name: store.i18n.t("listVehicleGroups.column.label.name"),
                key: "label",
                minWidth: 100,
            },
            {
                fieldName: "assignedVehicleCount",
                name: store.i18n.t("listVehicleGroups.column.assignedVehicleCount.name"),
                key: "assignedVehicleCount",
                minWidth: 80,
            },
            {
                fieldName: "assignedUserGroupCount",
                name: store.i18n.t("listVehicleGroups.column.assignedUserGroupCount.name"),
                key: "assignedUserGroupCount",
                minWidth: 150,
            },
        ];
    }

    /**
     * The items that should be shown in the list of added Users.
     */
    @computed private get userItems(): object[] {
        const users = [];

        for (const id of this.selectedUsers) {
            const user = store.repositoryUsers.byId(id);
            if (user !== undefined) {
                users.push({
                    key: user.id,
                    employeeId: user.employeeId,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    jobTitle: user.jobTitle,
                    fleetRole: store.i18n.formatFleetRole(user.fleetRole),
                });
            }
        }

        // Sort the users by last name.
        users.sort((a, b) => a.lastName.localeCompare(b.lastName));
        return users;
    }

    /**
     * The columns that should be shown in the list of selected Users.
     */
    @computed private get userColumns(): IColumn[] {
        return [
            {
                fieldName: "employeeId",
                name: store.i18n.t("listUsers.column.employeeId.name"),
                key: "employeeId",
                minWidth: 200,
                maxWidth: 300,
            },
            {
                fieldName: "firstName",
                name: store.i18n.t("listUsers.column.firstName.name"),
                key: "firstName",
                minWidth: 80,
                maxWidth: 100,
            },
            {
                fieldName: "lastName",
                name: store.i18n.t("listUsers.column.lastName.name"),
                key: "lastName",
                minWidth: 100,
                maxWidth: 150,
            },
            {
                fieldName: "jobtitle",
                name: store.i18n.t("listUsers.column.jobTitle.name"),
                key: "jobTitle",
                minWidth: 100,
                maxWidth: 150,
            },
            {
                fieldName: "fleetRole",
                name: store.i18n.t("listUsers.column.fleetRole.name"),
                key: "fleetRole",
                minWidth: 120,
                maxWidth: 200,
            },
        ];
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateUserGroup.submit.text")}
                validation={store.repositoryUserGroups.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateUserGroup}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdateUserGroup.label.label")}
                        disabled={!this.userGroup}
                        {...doubleBindString(this.userGroup!, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryUserGroups.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                <Stack
                    horizontal
                    styles={{
                        root: {
                            minWidth: "1200px",
                            minHeight: "400px",
                            padding: sizes.xl,
                        },
                    }}
                >
                    <Stack.Item tokens={{ padding: `0px 0px 0px ${sizes.m}` }}>
                        <MultiSelectWithList
                            repository={store.repositoryUsers}
                            query={{
                                pageSize: defaultPageSize,
                                sortKey: UserSortKey.LAST_NAME,
                                sortDirection: SortDirection.ASCENDING,
                            }}
                            onAdd={this.addUsers}
                            onDelete={this.removeUsers}
                            listStateId={this.userListStateId}
                            listState={store.serviceListStates.users}
                            addedEntities={this.selectedUsers}
                            columns={this.userColumns}
                            items={this.userItems}
                            formatEntity={(user: User) => store.i18n.formatUserFullName(user)}
                            addButtonText={store.i18n.t("component.multiSelect.addButton")}
                            removeButtonText={store.i18n.t("component.multiSelect.removeButton")}
                            dropdownLabel={store.i18n.t("formCreateUserGroup.user.label")}
                            multiSelectPlaceholder={store.i18n.t(
                                "formCreateUserGroup.user.placeholder",
                            )}
                        />
                    </Stack.Item>
                    <Stack.Item tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                        <Separator vertical />
                    </Stack.Item>
                    <Stack.Item tokens={{ padding: `0px ${sizes.m} 0px 0px` }}>
                        <MultiSelectWithList
                            repository={store.repositoryVehicleGroups}
                            query={{
                                pageSize: defaultPageSize,
                            }}
                            onAdd={this.addVehicleGroups}
                            onDelete={this.removeVehicleGroups}
                            listStateId={this.vehicleGroupListStateId}
                            listState={store.serviceListStates.vehicleGroups}
                            addedEntities={this.selectedVehicleGroups}
                            columns={this.vehicleGroupColumns}
                            items={this.vehicleGroupItems}
                            formatEntity={(group: VehicleGroup) => group.label}
                            addButtonText={store.i18n.t("component.multiSelect.addButton")}
                            removeButtonText={store.i18n.t("component.multiSelect.removeButton")}
                            dropdownLabel={store.i18n.t("formCreateUserGroup.vehicleGroup.label")}
                            multiSelectPlaceholder={store.i18n.t(
                                "formCreateUserGroup.vehicleGroup.placeholder",
                            )}
                        />
                    </Stack.Item>
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateUserGroup.cancel.text")}
                            onClick={this.showConfirmAndCallDialogCancelCallback}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
                <ModalConfirmation
                    isOpen={this.cancelConfirmationModalVisible}
                    title={store.i18n.t("modalAbortUpdate.title")}
                    text={store.i18n.t("modalAbortUpdate.description")}
                    onConfirm={this.closeForm}
                    onCancel={this.closeCancelConfirmationModalVisible}
                />
            </form>
        );
    }
}
